import { WEBAPP_DISPLAY_PREFIX, webappDisplayUrl } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Input } from "@sagot/ui";
import type { InputHTMLAttributes } from "react";
import { useSlugAvailability } from "../lib/slug";

function SlugStatusLine({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const status = useSlugAvailability(slug);
  if (status === "checking") return <>{t("onboarding.org.slugChecking")}</>;
  if (status === "available") return <span className="font-semibold text-success">{t("onboarding.org.slugAvailable")}</span>;
  if (status === "taken") return <span className="font-semibold text-danger">{t("onboarding.org.slugTaken")}</span>;
  if (status === "reserved") return <span className="font-semibold text-danger">{t("onboarding.org.slugReserved")}</span>;
  return <>{t("onboarding.org.addressHint")}</>;
}

interface WorkspaceAddressProps {
  /** Existing workspace slug (read-only preview) — or the editable slug input when creating. */
  slug: string;
  editable: boolean;
  error?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

/** "Workspace address": `app.<root>/chat/<slug>` (DEC-040 single origin — never `<slug>.<root>`). */
export function WorkspaceAddress({ slug, editable, error, inputProps }: WorkspaceAddressProps) {
  const { t } = useTranslation();
  if (!editable) {
    return (
      <Field label={t("onboarding.org.address")} hint={t("onboarding.org.addressHint")}>
        <Input readOnly value={webappDisplayUrl(slug)} data-testid="workspace-address" className="bg-surface-2 font-mono text-xs text-fg-muted" />
      </Field>
    );
  }
  return (
    <Field label={t("onboarding.org.address")} hint={<SlugStatusLine slug={slug} />} error={error}>
      <div className="flex">
        <span data-testid="workspace-address-prefix" className="inline-flex h-10 max-w-[60%] items-center truncate rounded-l-md border border-r-0 border-line-strong bg-sunken px-3 font-mono text-xs text-fg-subtle">
          {WEBAPP_DISPLAY_PREFIX}
        </span>
        <Input className="min-w-0 rounded-l-none font-mono text-[13px]" autoComplete="off" {...inputProps} />
      </div>
    </Field>
  );
}
